import { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Controller,
  ResolverOptions,
  ResolverResult,
  useForm,
} from 'react-hook-form';
import { useFetcher, useLocation, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, TextField, Typography } from '@mui/material';
import { parse, isValid, isAfter, startOfDay } from 'date-fns';
import { z } from 'zod';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { ResponsiveFormRow } from '@ldt/features/orders';
import { FormSectionCard } from '@ldt/features/orders/components/form_section_card';
import { LdtOrderFormHeader } from '@ldt/features/orders/components/order_form_header';
import { OrderingProviderFormSection } from '@ldt/features/orders/components/ordering_provider_form_section';
import {
  PatientInformationFormSchema,
  PatientInformationFormSection,
} from '@ldt/features/orders/components/patient_information_form_section';
import {
  createLdtPostRpTStageAndSurgicalMarginFieldsSchema,
  LdtPostRpTStageAndSurgicalMarginFields,
} from '@ldt/features/orders/components/post_rp_t_stage_and_surgical_margin_form_fields';
import { useOrderingProviderFormSection } from '@ldt/features/orders/hooks/use_ordering_provider_form_section';
import { OrderingProviderForm } from '@ldt/features/orders/ordering_provider_form';
import { FormDatePicker } from 'src/components/date_picker';
import { DecimalInput } from 'src/components/decimal_input';
import { FeedbackPopup, FeedbackType } from 'src/components/feedback_popup';
import { Caution } from 'src/components/icons/caution';
import { SectionHeader } from 'src/components/section_header';
import {
  GleasonScoreFields,
  REQUIRED_GLEASON_SCORE_FIELDS_SCHEMA,
} from 'src/distribution_platforms/features/components/gleason_score_form_fields';
import {
  processFetcherResponse,
  safeEmptyToUndefined,
  safeParseInt,
} from 'src/distribution_platforms/features/order_form';
import { hasSemanticFormChanges } from 'src/distribution_platforms/features/utils/patient_utils';
import { useDebounce } from 'src/hooks/use_debounce';
import {
  Ethnicity,
  LdtOrderForReviseReportFieldsFragment,
  ReviseReportInput,
  SpecimenType,
  SurgicalMargin,
  TStage,
} from 'src/types';
import { OrderAction } from 'src/types/loaders';
import { notifyError, notifySuccess } from 'src/utils';

const FORM_CONSTANTS = {
  WIDTH: '100%',
  MIN_AGE: 18,
  MAX_AGE: 100,
  MAX_PSA: 30,
  PSA_MIN: 0,
} as const;

/** Field keys excluded from "must revise at least 1 field" check (e.g. revision reason is required and will always be dirty to submit) */
const FIELDS_EXCLUDED_FROM_REVISE_ONE_CHECK = new Set<string>([
  'revisionReason',
]);

// Type for form data - inferred from the schema created with default values
export type LdtProstatePostRPReviseReportFormData = z.infer<
  ReturnType<typeof createLdtProstatePostRPReviseReportFormSchema>
>;

// Function to create schema with conditional NCCN requirement
export const createLdtProstatePostRPReviseReportFormSchema = () =>
  z.object({
    // Patient Information
    ...PatientInformationFormSchema.shape,

    // Ordering Provider
    orderingProviderId: z.string().min(1, 'Ordering Provider is required'),

    // Clinical Information
    ...REQUIRED_GLEASON_SCORE_FIELDS_SCHEMA.shape,
    baselinePsa: z
      .number({
        error: (issue) => {
          if (issue.code === 'invalid_type') {
            return { message: 'PSA level is required' };
          }
          return null;
        },
      })
      .min(
        FORM_CONSTANTS.PSA_MIN,
        `PSA must be at least ${FORM_CONSTANTS.PSA_MIN} ng/mL`,
      )
      .max(
        FORM_CONSTANTS.MAX_PSA,
        `PSA must not exceed ${FORM_CONSTANTS.MAX_PSA} ng/mL`,
      )
      .nullable()
      .superRefine((val, ctx) => {
        if (val === null) {
          ctx.addIssue({ code: 'custom', message: 'PSA level is required' });
        }
      }),
    ...createLdtPostRpTStageAndSurgicalMarginFieldsSchema().shape,

    dateOfRP: z
      .string()
      .min(1, 'Date of RP is required')
      .refine(
        (val) => {
          if (!val || !val.trim()) return false;
          try {
            const date = parse(val.trim(), 'yyyy-MM-dd', new Date());
            if (!isValid(date)) return false;
            return !isAfter(date, startOfDay(new Date()));
          } catch {
            return false;
          }
        },
        { message: 'Date of RP cannot be a future date' },
      ),

    // Specimen ID - required, allowed: alphanumeric, dash, underscore, dot, comma, parentheses, colon, semicolon, space, slash
    specimenId: z
      .string()
      .min(1, 'Specimen ID is required')
      .regex(
        /^[a-zA-Z0-9\-_.(),:; /]+$/,
        'Allowed characters: alphanumeric, dash, underscore, dot, comma, parentheses, colon, semicolon, space, slash',
      ),

    // Revision Reason - required, always default ''
    revisionReason: z
      .string()
      .min(1, 'Revision Reason is required')
      .max(400, 'Revision Reason must be at most 400 characters'),
  });

// Custom resolver - only handles blocking errors for CS users
export const createCustomResolver =
  (warningsRef?: {
    current: Partial<
      Record<keyof LdtProstatePostRPReviseReportFormData, string>
    >;
  }) =>
  async (
    data: LdtProstatePostRPReviseReportFormData,
    context: unknown,
    options: ResolverOptions<LdtProstatePostRPReviseReportFormData>,
  ) => {
    const schema = createLdtProstatePostRPReviseReportFormSchema();
    const zodResult = await zodResolver(schema)(data, context, options);

    // Manual cross-field validation (blocking errors only)
    const customErrors = { ...zodResult.errors };

    // Compute warnings (non-blocking) - these will be returned from the resolver
    const warnings: Partial<
      Record<keyof LdtProstatePostRPReviseReportFormData, string>
    > = {};

    // PSA warning
    const psaWarning = computePsaWarning(data.baselinePsa ?? undefined);
    if (psaWarning) {
      warnings.baselinePsa = psaWarning;
    }

    const result = {
      values: data,
      errors: customErrors,
      warnings: warnings,
    } as ResolverResult<LdtProstatePostRPReviseReportFormData> & {
      warnings: Partial<
        Record<keyof LdtProstatePostRPReviseReportFormData, string>
      >;
    };

    if (warningsRef) {
      warningsRef.current = result.warnings;
    }

    return result;
  };

// Helper function to compute PSA warning
const computePsaWarning = (psaValue?: number): string => {
  if (!psaValue || psaValue <= 5) return '';
  return 'PSA exceeds 5.00 ng/mL. Please verify the provided PSA is the most recent PSA after prostatectomy.';
};

// Helper function to create default form values from Order
export const createDefaultFormValues = (
  order?: LdtOrderForReviseReportFieldsFragment | null,
): LdtProstatePostRPReviseReportFormData => {
  return {
    firstName: order?.patient?.firstNamePhi?.trim() ?? '',
    lastName: order?.patient?.lastNamePhi?.trim() ?? '',
    dateOfBirth: order?.patient?.dateOfBirthPhi ?? '',
    ethnicity: (order?.patient?.ethnicity ?? '') as Ethnicity,
    orderingProviderId: order?.orderingProvider?.id ?? '',
    primaryGleasonScore: (order?.primaryGleasonScore?.toString() ?? '') as
      | '3'
      | '4'
      | '5',
    secondaryGleasonScore: (order?.secondaryGleasonScore?.toString() ?? '') as
      | '3'
      | '4'
      | '5',
    baselinePsa: order?.psa ?? null,
    tStage: (order?.tStage ?? '') as TStage,
    surgicalMargin: (order?.surgicalMargin ?? '') as SurgicalMargin,
    dateOfRP: order?.labProcedureDatePhi ?? '',
    specimenId: order?.specimenId?.trim() ?? '',
    revisionReason: '',
  };
};

export const LdtProstatePostRPReviseReportForm: FC<{
  order: LdtOrderForReviseReportFieldsFragment;
  ldtOrderRepository: LdtOrderRepository;
}> = ({ order, ldtOrderRepository }) => {
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const location = useLocation();

  // Ref to store warnings computed in the resolver
  const warningsRef = useRef<
    Partial<Record<keyof LdtProstatePostRPReviseReportFormData, string>>
  >({});

  const defaultValues = createDefaultFormValues(order);

  const {
    control,
    formState: { errors },
    trigger,
    getValues,
    setValue,
    watch,
  } = useForm<LdtProstatePostRPReviseReportFormData>({
    resolver: createCustomResolver(warningsRef),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const {
    selectedOrderingProvider,
    hasOrderingProviderChanges,
    orderingProviderSectionProps,
    orderingProviderDialogProps,
  } = useOrderingProviderFormSection({
    ldtOrderRepository,
    isOrderingPhysicianUser: false,
    initialOrderingProvider: order.orderingProvider ?? null,
    setOrderingProviderId: (id, options) =>
      setValue('orderingProviderId', id, options),
    triggerOrderingProviderValidation: () => trigger('orderingProviderId'),
  });

  const currentValues = watch();

  // Run validation once on mount so resolver populates warnings and they display immediately when the form opens.
  // Exclude revision reason so it is not validated until the user interacts with it.
  useEffect(() => {
    const fieldsToValidate = (
      Object.keys(
        defaultValues,
      ) as (keyof LdtProstatePostRPReviseReportFormData)[]
    ).filter((name) => !FIELDS_EXCLUDED_FROM_REVISE_ONE_CHECK.has(name));
    void trigger(fieldsToValidate);
    // defaultValues is stable for the lifetime of the form (derived from order on mount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  // Debounced validation triggers for performance optimization
  const debouncedPsaValidation = useDebounce(() => {
    trigger('baselinePsa');
  }, 300);

  // Build ReviseReportInput for the API (required by loader / reviseReport mutation)
  const getReviseReportInputFromFormData =
    useCallback((): ReviseReportInput => {
      const formData = getValues();

      return {
        orderId: order.id,
        orderingProviderId: formData.orderingProviderId,
        patientDateOfBirthPhi: formData.dateOfBirth,
        patientEthnicity: (safeEmptyToUndefined(formData.ethnicity) ??
          null) as Ethnicity | null,
        patientFirstNamePhi: formData.firstName.trim(),
        patientLastNamePhi: formData.lastName.trim(),
        reason: formData.revisionReason?.trim(),
        labProcedureDatePhi: formData.dateOfRP,
        specimenId: formData.specimenId.trim(),
        prostatePostRpData: {
          primaryGleasonScore: safeParseInt(formData.primaryGleasonScore)!,
          secondaryGleasonScore: safeParseInt(formData.secondaryGleasonScore)!,
          psa: formData.baselinePsa!,
          surgicalMargin: formData.surgicalMargin,
          tStage: formData.tStage,
        },
        metastaticProstateData: null,
        prostateBiopsyData: null,
      };
    }, [getValues, order]);

  // Handle action results
  useEffect(() => {
    const result = processFetcherResponse(fetcher.data, fetcher.state);

    if (result) {
      if (result.type === 'success') {
        notifySuccess(result.message);
      } else if (result.type === 'error') {
        notifyError(result.message);
      }
    }
  }, [fetcher.data, fetcher.state]);

  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [showReviseAtLeastOneError, setShowReviseAtLeastOneError] =
    useState(false);

  const hasRelevantDirtyFields =
    hasSemanticFormChanges(
      currentValues,
      defaultValues,
      FIELDS_EXCLUDED_FROM_REVISE_ONE_CHECK,
    ) || hasOrderingProviderChanges;

  useEffect(() => {
    if (hasRelevantDirtyFields && showReviseAtLeastOneError) {
      setShowReviseAtLeastOneError(false);
    }
  }, [hasRelevantDirtyFields, showReviseAtLeastOneError]);

  const onSubmitClick = async () => {
    setShowReviseAtLeastOneError(false);
    const isValid = await trigger();
    if (!isValid) return;
    if (!hasRelevantDirtyFields) {
      setShowReviseAtLeastOneError(true);
      return;
    }
    setSubmitDialogOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setSubmitDialogOpen(false);
    const input = getReviseReportInputFromFormData();
    const formData = new FormData();
    formData.append('action', OrderAction.REVISE_REPORT);
    formData.append('reviseReportInput', JSON.stringify(input));
    fetcher.submit(formData, {
      method: 'post',
      action: location.pathname,
    });
  };

  const handleCloseConfirmSubmitDialog = () => {
    setSubmitDialogOpen(false);
  };

  return (
    <Box
      component='form'
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmitClick();
      }}
      noValidate
      sx={{
        width: '100%',
        height: '100%',
        maxWidth: '2000px',
        maxHeight: '1500px',
        alignSelf: 'center',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <LdtOrderFormHeader
        specimenType={SpecimenType.ProstatePostRp}
        onClose={() => navigate(-1)}
        crossOrgId={order.crossOrgId}
        title='Revise Report'
      />
      <Typography
        variant='caption'
        sx={{
          color: 'text.secondary',
        }}
      >
        *Indicates a required field
      </Typography>
      {/* Scrollable content area */}
      <Box sx={{ flex: 1, overflow: 'auto', mt: 2 }}>
        {/* Physician Section */}
        <OrderingProviderFormSection
          control={control}
          errors={errors}
          trigger={trigger}
          ldtOrderRepository={ldtOrderRepository}
          selectedOrderingProvider={selectedOrderingProvider}
          {...orderingProviderSectionProps}
        />

        {/* Patient Information Section */}
        <PatientInformationFormSection control={control} errors={errors} />

        {/* Clinical & Pathology Information Section */}
        <FormSectionCard
          header={<SectionHeader title='Clinical & Pathology Information' />}
          sx={{ borderBottom: 'none' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {/* Gleason Scores Row */}
            <GleasonScoreFields
              control={control}
              errors={errors}
              trigger={trigger}
            />

            {/* PSA and T-Stage Row */}
            <ResponsiveFormRow>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='baselinePsa'
                  control={control}
                  render={({ field }) => {
                    const psaValue = field.value;
                    const hasError = !!errors.baselinePsa;
                    // Get warning from resolver (computed in resolver)
                    const warningText = warningsRef.current.baselinePsa;

                    return (
                      <DecimalInput
                        required
                        fullWidth
                        label='Most Recent PSA after prostatectomy (ng/mL)'
                        value={psaValue}
                        decimalPlaces={2}
                        min={FORM_CONSTANTS.PSA_MIN}
                        max={FORM_CONSTANTS.MAX_PSA}
                        error={hasError}
                        validationText={errors.baselinePsa?.message}
                        warningText={warningText}
                        onBlur={field.onBlur}
                        onChange={(value) => {
                          field.onChange(value);
                          // Debounced PSA validation - triggers after 300ms of no typing
                          debouncedPsaValidation();
                        }}
                      />
                    );
                  }}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                }}
              />
            </ResponsiveFormRow>
            <LdtPostRpTStageAndSurgicalMarginFields
              control={control}
              errors={errors}
              trigger={trigger}
            />

            {/* Date of RP & Specimen ID */}
            <ResponsiveFormRow>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='dateOfRP'
                  control={control}
                  render={({ field }) => (
                    <FormDatePicker
                      label='Date of RP'
                      maxDate={new Date()}
                      value={field.value}
                      required
                      fullWidth
                      error={!!errors.dateOfRP}
                      helperText={errors.dateOfRP?.message || ' '}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='specimenId'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      fullWidth
                      label='Specimen ID'
                      error={!!errors.specimenId}
                      helperText={errors.specimenId?.message || ' '}
                    />
                  )}
                />
              </Box>
            </ResponsiveFormRow>
          </Box>
        </FormSectionCard>

        {/* Revision Reason - required, default always '' */}
        <FormSectionCard
          header={<SectionHeader title='Revision Reason' />}
          sx={{ borderBottom: 'none' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Controller
              name='revisionReason'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  multiline
                  rows={4}
                  placeholder='Type here...'
                  error={!!errors.revisionReason}
                  helperText={errors.revisionReason?.message || ' '}
                  slotProps={{
                    input: {
                      inputProps: { maxLength: 400 },
                    },
                  }}
                />
              )}
            />
          </Box>
        </FormSectionCard>
      </Box>
      {/* Fixed form actions at the bottom - single Submit button */}
      <Box
        sx={{
          flexShrink: 0,
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-end' },
          }}
        >
          <Button
            type='submit'
            variant='contained'
            color='primary'
            size='large'
            disabled={fetcher.state !== 'idle'}
            sx={{
              width: { xs: '100%', sm: '160px' },
              maxWidth: 350,
              minHeight: { xs: '48px', sm: 'auto' },
            }}
          >
            {fetcher.state !== 'idle' ? 'SUBMITTING...' : 'SUBMIT'}
          </Button>
          <Typography variant='body2' color='error' role='alert'>
            {showReviseAtLeastOneError
              ? 'You must revise at least 1 field.'
              : ''}
          </Typography>
        </Box>
      </Box>
      <FeedbackPopup
        data={{
          primaryButtonText: 'YES, I CONFIRM',
          secondaryButtonText: 'NO',
          title: 'Confirm Revise Report?',
          subtitle: 'I have reviewed and confirmed the information is correct.',
          type: FeedbackType.Primary,
          icon: Caution,
        }}
        isDialogOpen={submitDialogOpen}
        onPrimaryButtonClick={handleConfirmSubmit}
        onSecondaryButtonClick={handleCloseConfirmSubmitDialog}
        handleDialogClose={handleCloseConfirmSubmitDialog}
      />

      {orderingProviderDialogProps.open && (
        <OrderingProviderForm
          open
          orderRepository={ldtOrderRepository}
          onClose={orderingProviderDialogProps.onClose}
          initialProvider={orderingProviderDialogProps.initialProvider}
          onSuccess={orderingProviderDialogProps.onSuccess}
          onDelete={orderingProviderDialogProps.onDelete}
        />
      )}
    </Box>
  );
};
